import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ordersApi, supportingDataApi, marketDataStalenessAPI } from '../../services/api';
import { Scheme } from '../../types/client';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { joinFieldErrors } from '../../utils/apiErrors';
import OrderSchemeSelector from '../order_management/OrderSchemeSelector';
import OrderClientSelector from '../order_management/OrderClientSelector';
import { useSchemeClients, clientIdsForScheme, pruneClientIds } from '@/hooks/useSchemeClients';
import FormCard, {
    FormSection,
    FormGrid,
    FormField,
    FormInput,
    FormSelect,
    FormReactAsyncSelect,
    FormHintError,
} from '../utils/FormCard';
import {
    CompanyOption, CASH_ENV_OPTIONS, CASH_ENV_HIGH, CASH_ENV_LOW, CASH_ENV_T0, CASH_ENV_PERCENTAGE, loadEtfCompanyOptions,
} from './etfOrderUtils';
import { SCHEME_REGION_GLOBAL } from '@/config/constants';

interface EtfOrderFormProps {
    type: 'buy' | 'sell';
}

interface EtfOrderFormData {
    scheme_ids: number[];
    company_id: number;
    cash_env: number;
    /** With the Percentage cash environment — buy: % of each client's AUM; sell: % of their ETF holding. */
    percentage?: number;
    limit_price?: number;
    /** Client-wise selection; a scheme with none of its clients picked orders for all of them. */
    client_ids: number[];
}

const CONFIG = {
    buy: {
        title: 'Create ETF Buy Order',
        submitLabel: 'Submit ETF Buy Order',
        cashEnvDefault: CASH_ENV_HIGH,
        percentageLabel: '% of AUM to Buy',
        percentageHint: "Each client buys this share of their AUM, capped at their available cash.",
        successMessage: (count: number) => `ETF buy order submitted for ${count} scheme(s)`,
        failMessage: 'Failed to submit ETF buy order',
    },
    sell: {
        title: 'Create ETF Sell Order',
        submitLabel: 'Submit ETF Sell Order',
        cashEnvDefault: CASH_ENV_LOW,
        percentageLabel: '% of ETF Holding to Sell',
        percentageHint: 'Each client sells this share of their holding of the ETF.',
        successMessage: (count: number) => `ETF sell order submitted for ${count} scheme(s)`,
        failMessage: 'Failed to submit ETF sell order',
    },
} as const;

const EtfOrderForm: React.FC<EtfOrderFormProps> = ({ type }) => {
    const navigate = useNavigate();
    const config = CONFIG[type];

    const {
        register,
        control,
        handleSubmit,
        setError,
        setValue,
        getValues,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<EtfOrderFormData>({
        defaultValues: {
            scheme_ids: [],
            company_id: 0,
            cash_env: config.cashEnvDefault,
            client_ids: [],
            ...(type === 'buy' ? { limit_price: 0 } : {}),
        },
    });

    const [schemes, setSchemes] = useState<Scheme[]>([]);
    const selectedSchemeIds = watch('scheme_ids');
    // The global region is what the backend's settlement_lag_days == 0 gate maps to.
    // OrderSchemeSelector keeps a selection within one region, so the first is enough.
    const isGlobalScheme =
        selectedSchemeIds.length > 0
        && schemes.find(s => s.id === selectedSchemeIds[0])?.region === SCHEME_REGION_GLOBAL;
    const isPercentage = watch('cash_env') === CASH_ENV_PERCENTAGE;
    // A global cash-environment sell names no company; a percentage needs the ETF it is a share of.
    const isGlobalSell = type === 'sell' && isGlobalScheme && !isPercentage;
    const clientWiseSchemeIds = useMemo(
        () => selectedSchemeIds.filter(id => schemes.find(s => s.id === id)?.allow_client_wise_orders),
        [selectedSchemeIds, schemes]
    );
    const { schemeClients, loading: clientsLoading } = useSchemeClients(clientWiseSchemeIds);
    const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // A global scheme settles same day, so its liquid ETF is spendable cash and no band
    // applies — T+0 is the only environment that means anything for it.
    useEffect(() => {
        setValue('cash_env', isGlobalScheme ? CASH_ENV_T0 : config.cashEnvDefault);
    }, [isGlobalScheme, config.cashEnvDefault, setValue]);

    // Live price → pre-fill limit price (buy only) — same behavior as BuyOrderForm.
    const companyId = watch('company_id');
    const [livePrice, setLivePrice] = useState<{ ltp: number | null; ltp_timestamp: string | null } | null>(null);
    const [livePriceLoading, setLivePriceLoading] = useState(false);
    useEffect(() => {
        if (type !== 'buy') return;
        if (!companyId) {
            setLivePrice(null);
            setValue('limit_price', 0);
            return;
        }
        let cancelled = false;
        setLivePriceLoading(true);
        setValue('limit_price', 0);
        marketDataStalenessAPI.getLivePrice(Number(companyId))
            .then(data => {
                if (cancelled) return;
                setLivePrice(data);
                if (data.ltp != null && getValues('limit_price') === 0) {
                    setValue('limit_price', data.ltp, { shouldValidate: true });
                }
            })
            .catch(() => { if (!cancelled) setLivePrice({ ltp: null, ltp_timestamp: null }); })
            .finally(() => { if (!cancelled) setLivePriceLoading(false); });
        return () => { cancelled = true; };
    }, [companyId, type, setValue]);

    useEffect(() => {
        let cancelled = false;
        supportingDataApi.getSchemes()
            .then(data => { if (!cancelled) setSchemes(data.filter(s => s.is_active)); })
            .catch(() => { if (!cancelled) setSubmitError('Failed to load schemes'); })
            .finally(() => { if (!cancelled) setDataLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const loadOptions = useCallback(
        loadEtfCompanyOptions((msg) => setSubmitError(msg)),
        [],
    );

    const onSubmit: SubmitHandler<EtfOrderFormData> = async (data) => {
        const { scheme_ids, limit_price, company_id, cash_env, percentage, client_ids: selectedClientIds } = data;
        setSubmitError(null);

        // Empty means every eligible client; a client-wise scheme gets only its picked clients.
        const clientIdsFor = (scheme_id: number) =>
            clientWiseSchemeIds.includes(scheme_id)
                ? clientIdsForScheme(scheme_id, selectedClientIds, schemeClients)
                : [];

        // A global sell names no company: the backend resolves the liquid instrument per
        // client from holdings. It still carries cash_env, so the stored order records
        // that it ran under T+0 rather than leaving the column null.
        const submitOne = (scheme_id: number) =>
            type === 'buy'
                ? ordersApi.submitEtfBuyOrder({
                    company_id, scheme_id, limit_price: limit_price as number,
                    cash_env, ...(isPercentage ? { aum_pct: percentage } : {}),
                    client_ids: clientIdsFor(scheme_id),
                })
                : ordersApi.submitEtfSellOrder({
                    scheme_id,
                    ...(isGlobalSell ? {} : { company_id }),
                    cash_env, ...(isPercentage ? { holding_pct: percentage } : {}),
                    client_ids: clientIdsFor(scheme_id),
                });

        const results = await Promise.allSettled(scheme_ids.map(submitOne));

        const failedIndices = results
            .map((r, i) => (r.status === 'rejected' ? i : null))
            .filter((i): i is number => i !== null);

        if (failedIndices.length === 0) {
            navigate('/etf-orders', { state: { message: config.successMessage(scheme_ids.length) } });
            return;
        }

        const failedCodes = failedIndices
            .map(i => schemes.find(s => s.id === scheme_ids[i])?.code ?? `ID ${scheme_ids[i]}`)
            .join(', ');

        const successCount = scheme_ids.length - failedIndices.length;
        if (successCount > 0) {
            setSubmitError(
                `${successCount} order(s) submitted successfully, but failed for: ${failedCodes}. ` +
                `Please retry the failed schemes.`,
            );
        } else {
            const firstError = (results[failedIndices[0]] as PromiseRejectedResult).reason;
            if (firstError?.response?.status === 400) {
                Object.entries(firstError.response.data).forEach(([field, messages]) => {
                    setError(field as keyof EtfOrderFormData, {
                        message: joinFieldErrors(messages),
                    });
                });
            } else {
                setSubmitError(firstError?.response?.data?.detail || firstError?.message || config.failMessage);
            }
        }
    };

    const livePriceHint = type === 'buy' && !!companyId
        ? livePriceLoading
            ? 'Fetching live price…'
            : livePrice?.ltp != null
                ? <>LTP: <strong>{formatCurrency(livePrice.ltp)}</strong>
                    {livePrice.ltp_timestamp && <> as of <strong>{formatDateTime(livePrice.ltp_timestamp)}</strong></>}
                  </>
                : livePrice != null
                    ? <FormHintError>Current price not found</FormHintError>
                    : null
        : null;

    return (
        <FormCard
            title={config.title}
            subtitle="Generate ETF orders across the selected schemes."
            backLabel="← Back to ETF Orders"
            onBack={() => navigate('/etf-orders')}
            errorBanner={submitError ?? undefined}
            submitLabel={isSubmitting ? 'Submitting…' : config.submitLabel}
            onSubmit={handleSubmit(onSubmit)}
            onCancel={() => navigate('/etf-orders')}
            submitting={isSubmitting}
            loading={dataLoading}
            loadingMessage="Loading form data…"
            variant="page"
        >
            <FormSection title="Order Details">
                <FormGrid>
                    <Controller
                        name="scheme_ids"
                        control={control}
                        rules={{ validate: ids => ids.length > 0 || 'At least one scheme is required' }}
                        render={({ field, fieldState }) => (
                            <OrderSchemeSelector
                                schemes={schemes}
                                value={field.value}
                                onChange={(newSchemeIds) => {
                                    field.onChange(newSchemeIds);
                                    // Clients belong to a scheme, so drop any the new scheme set no longer covers.
                                    setValue('client_ids', pruneClientIds(getValues('client_ids'), newSchemeIds, schemeClients));
                                }}
                                error={fieldState.error?.message}
                                disabled={isSubmitting}
                            />
                        )}
                    />

                    {clientWiseSchemeIds.length > 0 && (
                        <Controller
                            name="client_ids"
                            control={control}
                            render={({ field, fieldState }) => (
                                <OrderClientSelector
                                    schemes={schemes}
                                    schemeIds={clientWiseSchemeIds}
                                    schemeClients={schemeClients}
                                    value={field.value}
                                    onChange={field.onChange}
                                    loading={clientsLoading}
                                    hint="Leave empty to order for every eligible client. A scheme with none of its clients selected orders for all of them."
                                    error={fieldState.error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    )}

                    {!isGlobalSell && <Controller
                        name="company_id"
                        control={control}
                        rules={{ validate: v => isGlobalSell || !!v || 'Company is required' }}
                        render={({ field, fieldState }) => (
                            <FormField label="Company (ETF)" required error={fieldState.error?.message}>
                                <FormReactAsyncSelect
                                    value={selectedCompany}
                                    onChange={(opt) => {
                                        setSelectedCompany(opt);
                                        field.onChange(opt?.value ?? 0);
                                    }}
                                    loadOptions={loadOptions}
                                    placeholder="Search ETF company…"
                                    hasError={Boolean(fieldState.error)}
                                    isDisabled={isSubmitting}
                                />
                            </FormField>
                        )}
                    />}

                    <FormField
                        label="Cash Environment"
                        required
                        error={errors.cash_env?.message}
                        hint={isGlobalScheme
                            ? 'A global scheme settles same day, so its liquid ETF is spendable cash. '
                              + 'T+0 is the only band that applies to it; Percentage is also available.'
                            : undefined}
                    >
                        <FormSelect
                            {...register('cash_env', {
                                valueAsNumber: true,
                                validate: v => (v !== undefined && v !== null) || 'Cash environment is required',
                            })}
                            hasError={Boolean(errors.cash_env)}
                            disabled={isSubmitting}
                        >
                            {/* A band would send a global scheme down the per-scheme tier rules,
                                which it has no entry in — so T+0 is its only band. Percentage
                                uses no tier rules, so it stays available. */}
                            {(isGlobalScheme
                                ? CASH_ENV_OPTIONS.filter(opt => opt.value === CASH_ENV_T0 || opt.value === CASH_ENV_PERCENTAGE)
                                : CASH_ENV_OPTIONS
                            ).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </FormSelect>
                    </FormField>

                    {isPercentage && (
                        <FormField
                            label={config.percentageLabel}
                            required
                            error={errors.percentage?.message}
                            hint={config.percentageHint}
                        >
                            <FormInput
                                type="number"
                                {...register('percentage', {
                                    valueAsNumber: true,
                                    shouldUnregister: true,
                                    validate: v =>
                                        (v !== undefined && !isNaN(v) && v > 0 && v <= 100)
                                        || 'Enter a percentage above 0 and up to 100',
                                })}
                                step="0.01"
                                min="0.01"
                                max="100"
                                hasError={Boolean(errors.percentage)}
                                disabled={isSubmitting}
                            />
                        </FormField>
                    )}

                    {type === 'buy' && (
                        <FormField
                            label="Limit Price"
                            required
                            error={errors.limit_price?.message}
                            hint={livePriceHint}
                        >
                            <FormInput
                                type="number"
                                {...register('limit_price', {
                                    valueAsNumber: true,
                                    validate: v => (v !== undefined && !isNaN(v) && v > 0) || 'Limit price must be greater than 0',
                                })}
                                step="0.01"
                                min="0.01"
                                hasError={Boolean(errors.limit_price)}
                                disabled={isSubmitting}
                            />
                        </FormField>
                    )}
                </FormGrid>
            </FormSection>
        </FormCard>
    );
};

export default EtfOrderForm;
